import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row, Button, Form } from "react-bootstrap";
import Tooltip from "@mui/material/Tooltip";
import { allSharedComponents } from "../..";
import { competitionActions } from "../../../redux/actions";
import {
  translationConstants,
  Directions,
  competitionConstants,
  TableColumnStyle,
  genderEnumReversed,
} from "../../../constants";
import {
  changeTableSortIcons,
  removeSpace,
  allowOnlyNumbers,
  copyToClipboard,
} from "../../../system";
import { SearchBoxIcon } from "../../../assets/images/icon/iconsProvider";

const { DataTable } = allSharedComponents;

const initialGridState = {
  length: 0,
  pageNumber: 1,
  orderBy: "ranking asc",
  filterQuery: "isActive == true",
  search: null,
};

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#e8f1d2",
    },
  },
  cells: {
    style: {
      backgroundColor: "#f0f6e3 !important",
    },
  },
};

const CompetitionModal = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const participantsPageData = useSelector(
    (state) =>
      state.CompetitionReducer.competitionParticipantsDetail.pageData
        ?.competitionParticipants
  );

  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );

  const competitionData = useSelector(
    (state) => state.CompetitionReducer.competitionGeneralDetail
  );

  const sortingInProgressRef = useRef(false);

  const [rowData, setRowData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [formData, setFormData] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [boatClassFilter, setBoatClassFilter] = useState(
    competitionData?.classes?.[0]?.id || ""
  );

  const getcolumns = () => [
    {
      id: "id",
      accessor: "id",
      name: t(translationConstants.RECORD_ID),
      cell: (row) => (
        <Tooltip
          title={<div>{t(translationConstants.COPY_TO_CLIPBOARD)}</div>}
          placement="top"
          arrow
        >
          <div
            className="truncate-text cursor-pointer"
            onClick={() => copyToClipboard(row.id)}
          >
            {row.id}
          </div>
        </Tooltip>
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "name",
      name: t(translationConstants.name),
      selector: (row) =>
        direction === Directions.rtl
          ? removeSpace(row?.arabicName)
            ? row?.arabicName
            : row?.name
          : row?.name,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "email",
      name: t(translationConstants.Email),
      selector: (row) => row?.email,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "ranking",
      name: t(translationConstants.RANKING),
      selector: (row, index) => (
        <Form.Control
          type="number"
          value={row.ranking}
          className="score-modal-input"
          onKeyDown={(e) => allowOnlyNumbers(e)}
          onChange={(e) => {
            if (e.target.value >= 0) {
              handleInput(row, competitionConstants.RANKING, e.target.value);
            }
          }}
        />
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "points",
      name: t(translationConstants.SCORE),
      selector: (row, index) => (
        <Form.Control
          type="number"
          value={row.points}
          className="score-modal-input"
          onKeyDown={(e) => allowOnlyNumbers(e)}
          onChange={(e) => {
            if (e.target.value >= 0) {
              handleInput(row, competitionConstants.POINTS, e.target.value);
            }
          }}
        />
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
  ];

  const [columns, setColumns] = useState(getcolumns());

  useEffect(() => {
    setColumns(getcolumns());
    // eslint-disable-next-line
  }, [i18n.dir()]);

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    const sortedData = [...filteredData];
    sortedData.sort((a, b) => {
      const aValue = a[sortedColumn.id];
      const bValue = b[sortedColumn.id];
      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      } else {
        return aValue - bValue;
      }
    });

    if (sortedColumn.sortDirection === null) {
      sortedData.reverse();
    }
    setFilteredData(sortedData);
    setColumns(changeTableSortIcons(columns, sortedColumn));
  };

  useEffect(() => {
    dispatch(
      competitionActions.getCompetitionParticipantsDetailById(
        props.id,
        initialGridState
      )
    );
    //eslint-disable-next-line
  }, [dispatch, props.id]);

  useEffect(() => {
    if (participantsPageData) {
      setRowData(participantsPageData);
      setFilteredData(participantsPageData);
    }
    //eslint-disable-next-line
  }, [participantsPageData]);

  const applyFilters = () => {
    let filtered = rowData;

    if (searchValue) {
      filtered = filtered.filter(
        (row) =>
          row.name.includes(searchValue) || row.email.includes(searchValue)
      );
    }

    if (boatClassFilter) {
      filtered = filtered.filter((row) =>
        row.genderBoatClass?.id.includes(boatClassFilter)
      );
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    applyFilters();
    //eslint-disable-next-line
  }, [searchValue, boatClassFilter, rowData]);

  const handleSearch = (value) => setSearchValue(value);

  const handleBoatClassFilter = (value) => {
    setBoatClassFilter(value);
  };

  const handleInput = (row, type, value) => {
    setRowData((prevData) =>
      prevData.map((item) =>
        item.id === row.id
          ? { ...item, [type === competitionConstants.RANKING ? "ranking" : "points"]: value }
          : item
      )
    );
    setFilteredData((prevData) =>
      prevData.map((item) =>
        item.id === row.id
          ? { ...item, [type === competitionConstants.RANKING ? "ranking" : "points"]: value }
          : item
      )
    );
  };

  const handleSubmit = () => {
    const filteredData = rowData.map(({ id, ranking, points }) => ({
      competitionParticipantId: id,
      ranking,
      points,
    }));

    props.setShow(!props.show);
    dispatch(
      competitionActions.updateScore(
        props.id,
        filteredData,
        initialGridState,
        props.setIsSaveShow
      )
    );
  };

  return (
    <>
      <div className="ViewHeader">
        <p className="viewUserPara">{t(props.title)}</p>
      </div>
      <p className="horizentalLine" />

      <div className="">
        <Container className="mw-100 my-3">
          <Row>
            <Col
              md="6"
              xs="12"
              className="d-flex justify-content-sm-start justify-content-center"
            >
              <div className="searchInputDiv d-flex justify-content-center align-items-center w-100">
                <input
                  className="searchInput w-100"
                  type="text"
                  placeholder={t(translationConstants.Search)}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <SearchBoxIcon />
              </div>
            </Col>
            <Col
              md="4"
              xs="12"
              className="d-flex justify-content-sm-start justify-content-center"
            >
              <Form.Select
                className="custom-select-icon filter-style"
                aria-label="Default select example"
                value={boatClassFilter}
                onChange={(event) => handleBoatClassFilter(event.target.value)}
              >
                {competitionData?.classes?.map((object) => {
                  const name =
                    direction === Directions.rtl && object.arabicName
                      ? object.arabicName
                      : object.name;
                  const genderText = t(
                    translationConstants[genderEnumReversed[object.gender]]
                  );
                  return (
                    <option
                      key={object.id}
                      value={object.id}
                      className="capitalize-first"
                    >
                      {`${name} - ${genderText}`}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>
          </Row>
        </Container>
        <div className="competition-score-modal">
          <DataTable
            columns={columns}
            items={filteredData}
            onSort={onSort}
            pagination={false}
            customStyles={customStyles}
          />
        </div>
      </div>
      <div className="d-block d-sm-flex align-items-center justify-content-center my-4">
        <Row>
          <Col className="mt-1 d-flex justify-content-center">
            <Button
              className="approve-button mx-1"
              type="button"
              onClick={() => {
                handleSubmit();
              }}
            >
              {t(translationConstants.SAVE)}
            </Button>
          </Col>
          <Col className="mt-1 d-flex justify-content-center">
            <Button
              className="reject-button mx-1"
              type="button"
              onClick={() => {
                props.onCancel();
              }}
            >
              {t(translationConstants.cancel)}
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CompetitionModal;
